import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardContent, Button, CircularProgress, Chip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import { Alert } from '@material-ui/lab'
import {
    getOfacIngestStatus,
    triggerOfacIngest,
    uploadOfacIngest,
} from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

const statTile = { padding: 12, minWidth: 140, textAlign: 'center' }
const statValue = { fontSize: 28, fontWeight: 600, color: '#222' }
const statLabel = { fontSize: 12, color: '#666', marginTop: 4 }

const statusChipStyle = (status) => {
    switch (status) {
        case 'SUCCESS':
            return { background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }
        case 'RUNNING':
            return { background: 'var(--badge-review-bg)', color: 'var(--rox-lilac-ink)' }
        case 'FAILED':
            return { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }
        case 'SKIPPED_NOT_MODIFIED':
            return { background: '#f3e5f5', color: '#6a1b9a' }
        default:
            return { background: 'var(--bg-canvas)', color: '#555' }
    }
}

const formatTs = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

const OfacUsage = () => {
    const [ingest, setIngest] = useState(null)
    const [loading, setLoading] = useState(false)
    const [triggering, setTriggering] = useState(false)
    const fileInputRef = useRef(null)

    const fetch = useCallback(() => {
        setLoading(true)
        getOfacIngestStatus()()
            .then((ingestStatus) => {
                setIngest(ingestStatus)
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load OFAC status' }))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => { fetch() }, [fetch])

    // Auto-refresh every 15s while a run is in flight so the counts update live.
    useEffect(() => {
        if (ingest?.lastRun?.status !== 'RUNNING') return undefined
        const interval = setInterval(fetch, 15000)
        return () => clearInterval(interval)
    }, [ingest?.lastRun?.status, fetch])

    // Pick sdn.csv, alt.csv and add.csv together; each is matched by its filename.
    const onFilesSelected = (event) => {
        const selected = Array.from(event.target.files || [])
        event.target.value = ''  // allow re-uploading the same files
        if (!selected.length) return
        const byName = (key) => selected.find((f) => f.name.toLowerCase().includes(key))
        const files = { sdn: byName('sdn'), alt: byName('alt'), add: byName('add') }
        const missing = Object.keys(files).filter((k) => !files[k]).map((k) => `${k}.csv`)
        if (missing.length) {
            Toast({ type: 'error', message: `Select sdn.csv, alt.csv and add.csv together (missing ${missing.join(', ')})` })
            return
        }
        setTriggering(true)
        uploadOfacIngest(files)()
            .then(() => {
                Toast({ type: 'success', message: 'OFAC ingest started from uploaded files' })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409 ? 'An ingest is already running' : 'Failed to upload files'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const runIngest = (force = false) => {
        setTriggering(true)
        triggerOfacIngest({ force })()
            .then(() => {
                Toast({
                    type: 'success',
                    message: force
                        ? 'OFAC ingest started (force re-download)'
                        : 'OFAC ingest started',
                })
                fetch()
            })
            .catch((err) => {
                const msg = err?.response?.status === 409
                    ? 'An ingest is already running'
                    : 'Failed to trigger ingest'
                Toast({ type: 'error', message: msg })
            })
            .finally(() => setTriggering(false))
    }

    const lastRun = ingest?.lastRun
    const isRunning = lastRun?.status === 'RUNNING'

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>OFAC Sanctions (SDN) Usage</h4>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={fetch}
                    disabled={loading}
                    variant='outlined'
                    size='small'
                >
                    Refresh
                </Button>
            </div>

            <Alert severity='info' className='mb-3'>
                The U.S. Treasury (OFAC) republishes the Specially Designated Nationals (SDN) list
                frequently — often several times a week. The scheduler checks daily and only
                re-downloads when the source has changed; no-change days record
                <strong> SKIPPED_NOT_MODIFIED</strong> in ~1&nbsp;second. Primary names and every
                a.k.a. / f.k.a. alias are mirrored so a name search matches across all of them.
            </Alert>

            {loading && !ingest && (
                <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>
            )}

            {ingest && (
                <Card variant='outlined' className='mb-3'>
                    <CardContent>
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                            <h6 className='mb-0'>Local mirror &amp; ingest</h6>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => runIngest(false)}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                    color='primary'
                                >
                                    {isRunning ? 'Running…' : 'Check + ingest'}
                                </Button>
                                <Button
                                    startIcon={<CloudDownloadIcon />}
                                    onClick={() => runIngest(true)}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                >
                                    Force re-download
                                </Button>
                                <Button
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={triggering || isRunning}
                                    variant='outlined'
                                    size='small'
                                    title='Ingest from sdn.csv, alt.csv and add.csv downloaded from OFAC'
                                >
                                    Upload CSVs
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='.csv,text/csv'
                                    multiple
                                    onChange={onFilesSelected}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.totalRecords?.toLocaleString?.() ?? ingest.totalRecords ?? 0}</div>
                                <div style={statLabel}>Sanctioned records</div>
                            </div>
                            <div style={statTile}>
                                <div style={statValue}>{ingest.totalNameRows?.toLocaleString?.() ?? ingest.totalNameRows ?? 0}</div>
                                <div style={statLabel}>Names indexed (incl. aliases)</div>
                            </div>
                        </div>

                        {!lastRun && (
                            <div style={{ fontSize: 13, color: '#666' }}>No ingest run recorded yet.</div>
                        )}

                        {lastRun && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                    <strong style={{ fontSize: 13 }}>Last run:</strong>
                                    <Chip
                                        label={lastRun.status}
                                        size='small'
                                        style={statusChipStyle(lastRun.status)}
                                    />
                                    {isRunning && <CircularProgress size={16} />}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13 }}>
                                    <strong>Started:</strong><span>{formatTs(lastRun.startedAt)}</span>
                                    <strong>Finished:</strong><span>{formatTs(lastRun.finishedAt)}</span>
                                    <strong>Names upserted:</strong><span>{lastRun.recordsUpserted?.toLocaleString?.() ?? lastRun.recordsUpserted ?? 0}</span>
                                    <strong>Names removed:</strong><span>{lastRun.recordsRemoved?.toLocaleString?.() ?? lastRun.recordsRemoved ?? 0}</span>
                                    {lastRun.rowsRead != null && (
                                        <>
                                            <strong>Rows read:</strong>
                                            <span>{lastRun.rowsRead?.toLocaleString?.() ?? lastRun.rowsRead}</span>
                                        </>
                                    )}
                                    {lastRun.rowsDedupSkipped != null && (
                                        <>
                                            <strong>Rows skipped (dedup):</strong>
                                            <span>{lastRun.rowsDedupSkipped?.toLocaleString?.() ?? lastRun.rowsDedupSkipped}</span>
                                        </>
                                    )}
                                    {lastRun.rowsMalformedSkipped != null && (
                                        <>
                                            <strong>Rows skipped (malformed):</strong>
                                            <span>{lastRun.rowsMalformedSkipped?.toLocaleString?.() ?? lastRun.rowsMalformedSkipped}</span>
                                        </>
                                    )}
                                    {lastRun.sourceFilename && (
                                        <>
                                            <strong>Source file:</strong>
                                            <span style={{ wordBreak: 'break-word' }}>{lastRun.sourceFilename}</span>
                                        </>
                                    )}
                                    {lastRun.sourceLastModified && (
                                        <>
                                            <strong>Source Last-Modified:</strong>
                                            <span>{lastRun.sourceLastModified}</span>
                                        </>
                                    )}
                                    {lastRun.errorMessage && (
                                        <>
                                            <strong style={{ color: 'var(--status-declined)' }}>Error:</strong>
                                            <span style={{ color: 'var(--status-declined)', wordBreak: 'break-word' }}>{lastRun.errorMessage}</span>
                                        </>
                                    )}
                                </div>

                                {isRunning && (
                                    <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                                        Auto-refreshing every 15 seconds while the run is in flight.
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {ingest && (
                <Card variant='outlined'>
                    <CardContent>
                        <h6 className='mb-2'>Reference</h6>
                        <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                            <div><strong>Source:</strong> <a href='https://sanctionssearch.ofac.treas.gov/' target='_blank' rel='noopener noreferrer'>sanctionssearch.ofac.treas.gov</a> (bulk files: sdn.csv / alt.csv / add.csv)</div>
                            <div><strong>Cost:</strong> free — public CSV files, no API key required</div>
                            <div><strong>Scheduler:</strong> daily check (<code>roxwrite.scheduler.ofac-ingest.cron</code>)</div>
                            <div><strong>Cadence:</strong> OFAC updates the SDN list frequently; daily check is essentially free thanks to Last-Modified short-circuit</div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </>
    )
}

export default OfacUsage
